import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Typography,
  Button,
  TextField,
  IconButton,
  useTheme,
  useMediaQuery,
  Divider,
  CircularProgress
} from '@mui/material';
import { GoogleIcon } from 'icons/brandIcons';
import { VisibilityIcon, VisibilityOffIcon } from 'icons/materialIcons';
import { useAuth } from 'contexts/AuthContext';
import { checkUserExists } from 'apis/authApi';
import { validateEmail } from 'utils/validator';
import { createAccessorySession } from 'apis/accessoryApi';
import { isMobileDevice } from 'utils/deviceDetector';

const AccessorySignIn = ({ onSignInSuccess, accessoryData, serialNumber }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { login, googleSignIn, authState } = useAuth();
  const emailInputRef = useRef(null);

  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [emailError, setEmailError] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [error, setError] = useState('');
  const [checkingExistingAuth, setCheckingExistingAuth] = useState(true);

  const isDeviceMobile = isMobileDevice();

  useEffect(() => {
    const existingSession = sessionStorage.getItem('accessorySession');
    if (existingSession) {
      onSignInSuccess();
      return;
    }

    if (authState?.isAuthenticated && authState?.user?.userId) {
      handleSuccessfulLogin(authState.user.userId);
    } else if (authState?.isAuthenticated === false) {
      setCheckingExistingAuth(false);
    }

    if (!checkingExistingAuth) {
      emailInputRef.current?.focus();
    }
  }, [authState?.isAuthenticated]);

  const handleEmailChange = (event) => {
    const inputEmail = event.target.value;
    setEmail(inputEmail);
    setEmailError('');
    setError('');

    if (inputEmail && !validateEmail(inputEmail)) {
      setEmailError('Invalid email address!');
    }
  };

  const handlePasswordChange = (event) => {
    setPassword(event.target.value);
    setPasswordError('');
    setError('');
  };

  const handleSuccessfulLogin = async (userId) => {
    try {
      setLoading(true);
      await createAccessorySession(userId);
      onSignInSuccess();
    } catch (error) {
      setError('Session error. Please try again.');
      setCheckingExistingAuth(false);
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleSignIn = async () => {
    setLoading(true);
    try {
      const response = await googleSignIn();
      if (response.success) {
        await handleSuccessfulLogin(response.user.userId);
      } else {
        setError('Please register first to create your account');
      }
    } catch (error) {
      setError('Failed to sign in with Google');
    } finally {
      setLoading(false);
    }
  };

  const handleEmailSignIn = async (event) => {
    event.preventDefault();

    if (!email) {
      setEmailError('Email is required');
      return;
    }
    if (!validateEmail(email)) {
      setEmailError('Invalid email address!');
      return;
    }
    if (!password) {
      setPasswordError('Password is required');
      return;
    }

    setLoading(true);
    try {
      const userExists = await checkUserExists(email);
      if (!userExists) {
        setError('Please register first to create your account');
        setLoading(false);
        return;
      }

      const response = await login(email, password);

      if (response.success) {
        await handleSuccessfulLogin(response.user.userId);
      } else if (response.code === 'auth/email-not-verified') {
        setError('Email not verified. Please verify your email before signing in.');
      } else if (response.code === 'auth/invalid-credential') {
        setError('Invalid email or password. Please try again.');
      } else {
        setError('Failed to sign in with email');
      }
    } catch (error) {
      if (error.code === 'auth/email-not-verified') {
        setError('Email not verified. Please verify your email before signing in.');
      } else if (error.code === 'auth/invalid-credential') {
        setError('Invalid email or password. Please try again.');
      } else {
        setError(error.message || 'Failed to sign in');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleKeyDown = (event) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      handleEmailSignIn(event);
    }
  };

  if (checkingExistingAuth || (loading && !isDeviceMobile)) {
    return (
      <Box sx={{
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '100vh'
      }}>
        <CircularProgress size={40} sx={{ mb: 2 }} />
        <Typography variant="body2" color="text.secondary">
          Preparing accessory activation...
        </Typography>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '100vh',
        backgroundColor: '#fff',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          width: '100%',
          maxWidth: {
            xs: '100%',
            sm: '440px',
            md: '440px'
          },
          px: 2,
        }}
      >
        <Typography
          variant="h5"
          sx={{
            fontWeight: 'bold',
            textAlign: 'center',
            color: '#000',
            marginBottom: '16px',
            fontSize: {
              xs: '1.25rem',
              sm: '1.5rem'
            }
          }}
        >
          Sign In to Activate
        </Typography>

        {accessoryData && serialNumber && (
          <Typography
            variant="body1"
            sx={{
              textAlign: 'center',
              color: '#666',
              marginBottom: '24px',
              fontSize: '0.9rem',
            }}
          >
            Sign in to activate accessory {serialNumber}
          </Typography>
        )}

        <form onSubmit={handleEmailSignIn} style={{ width: '100%', maxWidth: '320px' }}>
          <Button
            fullWidth
            variant="outlined"
            onClick={handleGoogleSignIn}
            disabled={loading}
            startIcon={<GoogleIcon />}
            sx={{
              marginBottom: '24px',
              color: '#666',
              borderColor: '#ddd',
              borderRadius: '8px',
              padding: '12px',
              textTransform: 'none',
              fontWeight: '500',
              '&:hover': {
                backgroundColor: '#f5f5f5',
                borderColor: '#ccc',
              },
            }}
          >
            {loading ? <CircularProgress size={24} /> : 'Sign in with Google'}
          </Button>

          <Divider sx={{ mb: 3 }}>
            <Typography variant="body2" sx={{ color: '#666' }}>or</Typography>
          </Divider>

          <Box sx={{ mb: 3 }}>
            <Typography variant="body2" sx={{ fontSize: '0.8rem', fontWeight: 600, color: "#4f4f4f", mb: 1 }}>
              Email
            </Typography>
            <TextField
              fullWidth
              type="email"
              value={email}
              onChange={handleEmailChange}
              onKeyDown={handleKeyDown}
              inputRef={emailInputRef}
              placeholder="Enter your email"
              error={!!emailError}
              helperText={emailError}
              disabled={loading}
              sx={{
                backgroundColor: '#f7f7f7',
                '& .MuiOutlinedInput-root': {
                  '& fieldset': { border: 'none' },
                  '&:hover fieldset': { border: 'none' },
                  '&.Mui-focused fieldset': { border: 'none' },
                  '& input': { fontSize: '0.9rem' },
                  '& input::placeholder': {
                    fontSize: '0.8rem',
                    fontWeight: '300',
                  },
                },
              }}
            />
          </Box>

          <Box sx={{ mb: 3 }}>
            <Typography variant="body2" sx={{ fontSize: '0.8rem', fontWeight: 600, color: "#4f4f4f", mb: 1 }}>
              Password
            </Typography>
            <TextField
              fullWidth
              type={showPassword ? 'text' : 'password'}
              value={password}
              onChange={handlePasswordChange}
              onKeyDown={handleKeyDown}
              placeholder="Enter your password"
              error={!!passwordError}
              helperText={passwordError}
              disabled={loading}
              InputProps={{
                endAdornment: (
                  <IconButton
                    onClick={() => setShowPassword(!showPassword)}
                    edge="end"
                  >
                    {showPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                  </IconButton>
                ),
              }}
              sx={{
                backgroundColor: '#f7f7f7',
                '& .MuiOutlinedInput-root': {
                  '& fieldset': { border: 'none' },
                  '&:hover fieldset': { border: 'none' },
                  '&.Mui-focused fieldset': { border: 'none' },
                  '& input': { fontSize: '0.9rem' },
                  '& input::placeholder': {
                    fontSize: '0.8rem',
                    fontWeight: '300',
                  },
                },
              }}
            />
          </Box>

          {error && (
            <Typography
              sx={{
                color: 'error.main',
                fontSize: '0.8rem',
                fontWeight: 500,
                marginBottom: 2,
                textAlign: 'center'
              }}
            >
              {error}
            </Typography>
          )}

          <Button
            type="submit"
            fullWidth
            variant="contained"
            disabled={loading}
            sx={{
              backgroundColor: '#000',
              color: '#fff',
              fontWeight: '500',
              borderRadius: '8px',
              padding: '12px',
              textTransform: 'none',
              marginBottom: '24px',
              boxShadow: 'none',
              '&:hover': {
                backgroundColor: '#333',
              },
            }}
          >
            {loading ? <CircularProgress size={24} color="inherit" /> : 'Sign In'}
          </Button>

          <Typography
            variant="body2"
            sx={{
              backgroundColor: '#fff8e1',
              color: '#b58105',
              textAlign: 'center',
              fontSize: '0.8rem',
              fontWeight: 500,
              padding: '12px 16px',
              borderRadius: '8px',
              marginBottom: '16px',
              width: '100%'
            }}
          >
            Don't have an account? Please visit our website to create one for activating your device.
          </Typography>
        </form>
      </Box>
    </Box>
  );
};

export default AccessorySignIn;